import React, { useMemo, useState } from "react";
import {
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Bar,
  LineChart,
  Line,
} from "recharts";
import {
  Button,
  Card,
  CardBody,
  Tab,
  TabPanel,
  Tabs,
  TabsBody,
  TabsHeader,
} from "@material-tailwind/react";

const AnalysedReport = ({ data, handleReset, FilterBar }) => {
  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

  const INITIAL_FILTERS = {
    dateRange: { start: null, end: null },
    vendor: "All Vendors",
    currency: "All Currencies",
  };
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const extractUniqueValues = (data, key) => {
    return Array.from(new Set(data?.map((item) => item[key]))).sort();
  };

  const filterData = (data, filters) => {
    const { dateRange, vendor, currency } = filters;

    return data?.filter((item) => {
      const date = new Date(item["Invoice Date"]);

      const vendorMatch =
        vendor === "All Vendors" || item["Vendor Name"] === vendor;
      const currencyMatch =
        currency === "All Currencies" || item.Currency === currency;
      const dateMatch =
        (!dateRange.start || date >= new Date(dateRange.start)) &&
        (!dateRange.end || date <= new Date(dateRange.end));

      return vendorMatch && currencyMatch && dateMatch;
    });
  };

  const processChartData = (data) => {
    const paymentStatus = data?.reduce((acc, curr) => {
      acc[curr["Payment Status"]] = (acc[curr["Payment Status"]] || 0) + 1;
      return acc;
    }, {});

    const currencyExposure = data?.reduce((acc, curr) => {
      acc[curr["Currency"]] =
        (acc[curr["Currency"]] || 0) + parseFloat(curr["Invoice Amount"]);
      return acc;
    }, {});

    const today = new Date();
    const agingCategories = {
      "Current": 0,
      "1-30 Days": 0,
      "31-60 Days": 0,
      "60+ Days": 0,
    };

    data?.forEach((curr) => {
      const dueDate = new Date(curr["Due Date"]);
      const daysPastDue = (today - dueDate) / (1000 * 60 * 60 * 24);
      if (daysPastDue <= 0) {
        agingCategories["Current"] += 1;
      } else if (daysPastDue <= 30) {
        agingCategories["1-30 Days"] += 1;
      } else if (daysPastDue <= 60) {
        agingCategories["31-60 Days"] += 1;
      } else {
        agingCategories["60+ Days"] += 1;
      }
    });

    const monthlyTrends = data?.reduce((acc, curr) => {
      const month = new Date(curr["Invoice Date"]).toLocaleString("default", {
        month: "short",
      });
      acc[month] = acc[month] || { invoices: 0, payments: 0 };
      acc[month].invoices += 1;
      if (curr["Payment Status"] === "Paid") {
        acc[month].payments += 1;
      }
      return acc;
    }, {});

    const monthlyTrendData = Object.entries(monthlyTrends).map(
      ([month, { invoices, payments }]) => ({
        month,
        Invoices: invoices,
        Payments: payments,
      })
    );

    monthlyTrendData.sort((a, b) => {
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return months.indexOf(a.month) - months.indexOf(b.month);
    });
    return {
      paymentStatus: Object.entries(paymentStatus).map(([name, value]) => ({
        name,
        value,
      })),
      currencyExposureData: Object.entries(currencyExposure).map(
        ([currency, amount]) => ({ currency, Amount: Math.round(amount) })
      ),
      agingData: Object.entries(agingCategories).map(([category, value]) => ({
        category,
        Value: value,
      })),
      monthlyTrendData: monthlyTrendData,
    };
  };

  const calculateDPO = (data) => {
    const paidInvoices = data.filter(
      (invoice) =>
        invoice["Payment Status"] === "Paid" && invoice["Payment Date"]
    );
    const totalDays = paidInvoices.reduce((sum, invoice) => {
      const invoiceDate = new Date(invoice["Invoice Date"]);
      const paymentDate = new Date(invoice["Payment Date"]);
      const days = (paymentDate - invoiceDate) / (1000 * 60 * 60 * 24);
      return sum + days;
    }, 0);
    return paidInvoices.length > 0
      ? (totalDays / paidInvoices.length).toFixed(2)
      : 0;
  };

  const calculateLatePaymentRate = (data) => {
    const paidInvoices = data.filter(
      (invoice) =>
        invoice["Payment Status"] === "Paid" && invoice["Payment Date"]
    );
    const latePayments = paidInvoices.filter((invoice) => {
      const paymentDate = new Date(invoice["Payment Date"]);
      const dueDate = new Date(invoice["Due Date"]);
      return paymentDate > dueDate;
    });
    return paidInvoices.length > 0
      ? ((latePayments.length / paidInvoices.length) * 100).toFixed(2)
      : 0;
  };

  const calculateEarlyPaymentRate = (data) => {
    const paidInvoices = data.filter(
      (invoice) =>
        invoice["Payment Status"] === "Paid" && invoice["Payment Date"]
    );

    const earlyPayments = paidInvoices.filter((invoice) => {
      const paymentDate = new Date(invoice["Payment Date"]);
      const dueDate = new Date(invoice["Due Date"]);
      return paymentDate < dueDate;
    });

    return paidInvoices.length > 0
      ? Number(((earlyPayments.length / paidInvoices.length) * 100).toFixed(2))
      : 0;
  };

  const getTopVendorsByCurrency = (data) => {
    const vendorSpendByCurrency = data.reduce((acc, curr) => {
      const vendorName = curr["Vendor Name"];
      const currency = curr["Currency"];
      const invoiceAmount = parseFloat(curr["Invoice Amount"]) || 0;

      if (!acc[currency]) acc[currency] = {};

      acc[currency][vendorName] =
        (acc[currency][vendorName] || 0) + invoiceAmount;
      return acc;
    }, {});

    const topVendors = Object.entries(vendorSpendByCurrency)
      .map(([currency, vendors]) => {
        const topVendor = Object.entries(vendors)
          .map(([name, spend]) => ({
            name,
            Spend: Math.round(spend),
            currency,
          }))
          .sort((a, b) => b.Spend - a.Spend)[0];

        return topVendor;
      })
      .filter((vendor) => vendor.name && vendor.currency !== "undefined");

    return topVendors;
  };

  const vendors = useMemo(() => {
    const uniqueVendors = extractUniqueValues(data, "Vendor Name");
    return ["All Vendors", ...uniqueVendors];
  }, [data]);

  const currencies = useMemo(() => {
    const uniqueCurrencies = extractUniqueValues(data, "Currency");
    return ["All Currencies", ...uniqueCurrencies];
  }, [data]);

  const topVendors = useMemo(() => getTopVendorsByCurrency(data), [data]);

  const filteredData = useMemo(
    () => filterData(data, filters),
    [data, filters]
  );

  const chartData = useMemo(
    () => processChartData(filteredData),
    [filteredData]
  );

  const daysPayableOutstanding = useMemo(
    () => calculateDPO(filteredData),
    [filteredData]
  );
  const latePaymentRate = useMemo(
    () => calculateLatePaymentRate(filteredData),
    [filteredData]
  );

  const earlyPaymentRate = useMemo(
    () => calculateEarlyPaymentRate(filteredData),
    [filteredData]
  );

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold mb-2">AP Analytics Dashboard</h1>
        <Button onClick={() => handleReset()}>Upload New File</Button>
      </div>

      {FilterBar(filters, setFilters, vendors, currencies)}

      <Card className="border border-gray-300 mb-3">
        <CardBody className="flex flex-col gap-3">
          <p className="text-black font-bold">Days Payable Outstanding</p>
          <p className="text-2xl font-bold">{daysPayableOutstanding} Days</p>
        </CardBody>
      </Card>

      <Card className="border border-gray-300 mb-3">
        <CardBody className="flex flex-col gap-3">
          <p className="text-black font-bold">Late Payment Rate</p>
          <p className="text-2xl font-bold">{latePaymentRate}%</p>
        </CardBody>
      </Card>

      <Card className="border border-gray-300 mb-3">
        <CardBody className="flex flex-col gap-3">
          <p className="text-black font-bold">Processing Efficiency</p>
          <p className="text-2xl font-bold">85.7%</p>
        </CardBody>
      </Card>

      <Card className="border border-gray-300 mb-3">
        <CardBody>
          <p className="text-black font-bold">Payment Status Distribution</p>
          <PieChart width={400} height={300}>
            <Pie
              data={chartData.paymentStatus}
              cx={200}
              cy={150}
              innerRadius={60}
              outerRadius={100}
              paddingAngle={5}
              dataKey="value"
            >
              {chartData.paymentStatus.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </CardBody>
      </Card>

      <Card className="border border-gray-300 mb-3">
        <CardBody>
          <p className="text-black font-bold mb-5">Currency Exposure</p>
          <BarChart
            width={400}
            height={300}
            data={chartData.currencyExposureData}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="currency" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="Amount" fill="#8884d8" />
          </BarChart>
        </CardBody>
      </Card>

      <Card className="border border-gray-300 mb-3">
        <CardBody>
          <p className="text-black font-bold mb-5">Invoice Aging Analysis</p>
          <BarChart width={400} height={300} data={chartData.agingData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="category" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="Value" fill="#82ca9d" />
          </BarChart>
        </CardBody>
      </Card>

      <Card className="border border-gray-300 mb-3">
        <CardBody>
          <p className="text-black font-bold mb-5">Monthly Trends</p>
          <LineChart width={400} height={300} data={chartData.monthlyTrendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="Invoices" stroke="#8884d8" />
            <Line type="monotone" dataKey="Payments" stroke="#82ca9d" />
          </LineChart>
        </CardBody>
      </Card>

      <Tabs value="details" className="w-full">
        <TabsHeader>
          <Tab value="details">Detailed Metrics</Tab>
          <Tab value="vendor">Vendor Analysis</Tab>
          <Tab value="risk">Risk Analysis</Tab>
        </TabsHeader>

        <TabsBody>
          <TabPanel value="details">
            <Card className="border border-gray-300 mb-3">
              <CardBody className="flex flex-col gap-3">
                <p className="text-black font-bold">Average Processing Time</p>
                <p className="text-2xl font-bold">4.2 Days</p>
              </CardBody>
            </Card>
            <Card className="border border-gray-300 mb-3">
              <CardBody className="flex flex-col gap-3">
                <p className="text-black font-bold">Early Payment Rate</p>
                <p className="text-2xl font-bold">{earlyPaymentRate}%</p>
              </CardBody>
            </Card>
            <Card className="border border-gray-300 mb-3">
              <CardBody className="flex flex-col gap-3">
                <p className="text-black font-bold">Exception Rate</p>
                <p className="text-2xl font-bold">3.2%</p>
              </CardBody>
            </Card>
          </TabPanel>
        </TabsBody>

        <TabsBody>
          <TabPanel value="vendor">
            <Card className="border border-gray-300 mb-3">
              <CardBody>
                <p className="text-black font-bold mb-5">
                  Top Vendors by Spend
                </p>
                <BarChart width={400} height={300} data={topVendors}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip
                    formatter={(value, name, props) => [
                      `${value} (${props.payload.currency})`,
                      "Spend",
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="Spend" fill="#8884d8" />
                </BarChart>
              </CardBody>
            </Card>
          </TabPanel>
        </TabsBody>

        <TabsBody>
          <TabPanel value="risk">
            <Card className="border border-gray-300 mb-3">
              <CardBody>
                <p className="text-black font-bold">Currency Risk Analysis</p>
                <PieChart width={400} height={300}>
                  <Pie
                    data={chartData.currencyExposureData}
                    cx={200}
                    cy={150}
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="Amount"
                    nameKey="currency"
                  >
                    {chartData.currencyExposureData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </CardBody>
            </Card>
          </TabPanel>
        </TabsBody>
      </Tabs>
    </div>
  );
};

export default AnalysedReport;
